import { useState } from 'react';
import { Control, useFieldArray, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';

import { Basket } from '../../../baskets/types/basket.type';
import { Product } from '../../../products/types/product.type';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';
import { BasketQuoteItem, ProductQuoteItem, QuoteItem } from '../../types/quote.type';
import { CatalogBasketsList } from '../../../shared/components/Catalog/CatalogBasketsList.component';
import { CatalogProductsList } from '../../../shared/components/Catalog/CatalogProductsList.component';
import { CreateQuoteFormValues } from './CreateQuoteStepper.component';
import { QuoteItemsTable } from './QuoteItemsTable.component';

type Props = {
  control: Control<CreateQuoteFormValues>;
  watch: UseFormWatch<CreateQuoteFormValues>;
  setValue: UseFormSetValue<CreateQuoteFormValues>;
};

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
}));

function productToQuoteItem(product: Product): ProductQuoteItem {
  return {
    type: 'product',
    productId: product.id,
    name: product.name,
    ref: product.reference,
    price: product.price,
    quantity: 1,
    tvaRate: product.tvaRate,
    category: product.category.name,
    subcategory: product.subCategory?.name,
    photo: product.photo ?? undefined,
  };
}

function basketToQuoteItem(basket: Basket): BasketQuoteItem {
  return {
    type: 'basket',
    basketId: basket.id,
    name: basket.name,
    ref: basket.reference,
    price: basket.price,
    quantity: 1,
    tvaRate: basket.tvaRate,
    category: basket.category.name,
    subcategory: basket.subCategory?.name,
    photo: basket.photo || undefined,
  };
}

function sameItem(i: QuoteItem, other: QuoteItem): boolean {
  if (i.type === 'product' && other.type === 'product') return i.productId === other.productId;
  if (i.type === 'basket' && other.type === 'basket') return i.basketId === other.basketId;
  return false;
}

export function CatalogStep({ control, watch, setValue }: Props) {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<'products' | 'baskets'>('products');

  const { fields, append, remove } = useFieldArray<CreateQuoteFormValues, 'items', 'fieldId'>({
    control,
    name: 'items',
    keyName: 'fieldId',
  });
  const items = (watch('items') ?? []) as QuoteItem[];

  const handleAddProduct = (product: Product) => {
    const candidate = productToQuoteItem(product);
    const idx = items.findIndex((i) => sameItem(i, candidate));
    if (idx >= 0) {
      setValue(`items.${idx}.quantity`, (Number(items[idx].quantity) || 0) + 1, { shouldDirty: true });
    } else {
      append(candidate);
    }
  };

  const handleAddBasket = (basket: Basket) => {
    const candidate = basketToQuoteItem(basket);
    const idx = items.findIndex((i) => sameItem(i, candidate));
    if (idx >= 0) {
      setValue(`items.${idx}.quantity`, (Number(items[idx].quantity) || 0) + 1, { shouldDirty: true });
    } else {
      append(candidate);
    }
  };

  const onQuantityChange = (index: number, value: number) =>
    setValue(`items.${index}.quantity`, value, { shouldDirty: true });
  const onPriceChange = (index: number, value: number) =>
    setValue(`items.${index}.price`, value, { shouldDirty: true });

  return (
    <div className={classes.root}>
      <TabsController
        size="small"
        tabs={[
          { id: 'products', label: 'Produits', value: 'products' },
          { id: 'baskets', label: 'Paniers', value: 'baskets' },
        ]}
        currentTabId={activeTab}
        onTabChange={(v) => setActiveTab(v as 'products' | 'baskets')}
      />
      {activeTab === 'products' ? (
        <CatalogProductsList onAdd={handleAddProduct} />
      ) : (
        <CatalogBasketsList onAdd={handleAddBasket} />
      )}
      <QuoteItemsTable
        items={items}
        fields={fields}
        onRemove={remove}
        onQuantityChange={onQuantityChange}
        onPriceChange={onPriceChange}
      />
    </div>
  );
}
